/**
 * Route Helpers for Fulfillment Lifecycle Dashboard
 *
 * Categorization logic for grouping routes into status sections.
 */

import { AGING_FILTER_VALUES } from 'components/logistics/fulfillmentLifecycle/constants';

import { getPullTimeDelta } from './formatters';

/**
 * If `labelsAgingMinutes` is defined, it means the route is a Label Aging Route.
 *
 * @param route
 * @returns {boolean}
 */
export const isLabelAgingRoute = (route) =>
    route?.labelsAgingMinutes !== undefined && route?.labelsAgingMinutes !== null;

/**
 * If `packingAgingMinutes` is defined, it means the route is a Packing Aging Route.
 *
 * @param route
 * @returns {boolean}
 */
export const isPackingAgingRoute = (route) =>
    route?.packingAgingMinutes !== undefined && route?.packingAgingMinutes !== null;

export const normalizeAgingFilters = (values) =>
    [...new Set(Array.isArray(values) ? values : [])].sort((a, b) => a.localeCompare(b));

export const routeMatchesAgingFilters = (route, agingFilters) => {
    // Keep all the routes if no aging filters are specified
    if (!Array.isArray(agingFilters) || agingFilters.length === 0) return true;

    const checks = {
        [AGING_FILTER_VALUES.LABELS]: isLabelAgingRoute(route),
        [AGING_FILTER_VALUES.PACKING]: isPackingAgingRoute(route),
    };

    // Keep the route if it matches any of the specified aging filters
    return agingFilters.some((key) => checks[key]);
};

/**
 * Check if a route has started physical work (labels printed and beyond).
 *
 * A route is considered "work started" when at least one shipment has reached
 * the LABEL_PRINTED stage or beyond (packed, in transit, etc.).
 *
 * @param {Object} route - Route object with printedCount, packedCount, transitCount
 * @returns {boolean}
 */
export const hasWorkStarted = (route) => route.printedCount > 0 || route.packedCount > 0 || route.transitCount > 0;

/**
 * Sort routes by pull time ascending (earliest pull first).
 * Routes without a pull time sort to the top (treated as most urgent).
 *
 * @param {Object[]} routes - Array of route objects
 * @param {Date} now - Current time for delta calculation
 * @returns {Object[]} Sorted copy of the array
 */
const sortByPullTime = (routes, now) =>
    [...routes].sort((a, b) => {
        const deltaA = getPullTimeDelta(a.pullTime, now);
        const deltaB = getPullTimeDelta(b.pullTime, now);
        // null pull times sort to top (most urgent)
        if (deltaA === null && deltaB === null) return 0;
        if (deltaA === null) return -1;
        if (deltaB === null) return 1;
        // Higher delta = further past pull time = more urgent → sort descending by delta
        return deltaB - deltaA;
    });

/**
 * Categorize routes into status sections for the dashboard grid.
 *
 * Sections (status-based):
 * - complete: All shipments in transit (totalShipments > 0 && transitCount >= totalShipments)
 * - active:   Physical work has started — packing or transit in progress
 * - upcoming: No physical work yet (pre-purchase, label purchased, or label printed but not packed)
 * - unrouted: Reserved for future use (routes without line haul assignment)
 *
 * Within each section, routes are sorted by pull time (most urgent first).
 *
 * @param {Object[]} routes - Array of route objects
 * @param {Date} now - Current time for pull time sorting
 * @returns {{ active: Object[], upcoming: Object[], complete: Object[], unrouted: Object[] }}
 */
export const categorizeRoutes = (routes, now) => {
    const active = [];
    const upcoming = [];
    const complete = [];

    routes.forEach((route) => {
        if (route.totalShipments > 0 && route.transitCount >= route.totalShipments) {
            complete.push(route);
        } else if (hasWorkStarted(route)) {
            active.push(route);
        } else {
            upcoming.push(route);
        }
    });

    return {
        active: sortByPullTime(active, now),
        upcoming: sortByPullTime(upcoming, now),
        complete: sortByPullTime(complete, now),
        unrouted: [],
    };
};
